// import React, { useState, useEffect } from 'react';
import React, { useState, useEffect, useCallback } from 'react';

import Header from '../components/Header';
import SearchBar from '../components/SearchBar';
import JobCard from '../components/JobCard';
import JobFilters from '../components/JobFilters';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { jobsAPI, companiesAPI, categoriesAPI, locationsAPI } from '../services/api';
import { experienceLevels } from '../mockData';
import { ArrowRight, TrendingUp, MapPin, Briefcase, Loader2, Filter, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    experience: [],
    salary: [],
    jobType: [],
    industry: [],
    locationFilter: '',
    postedWithin: null,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  
  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [jobsRes, companiesRes, categoriesRes, locationsRes] = await Promise.all([
        jobsAPI.getAll(),
        companiesAPI.getAll(),
        categoriesAPI.getAll(),
        locationsAPI.getAll(),
      ]);
    
      // setJobs(jobsRes || []);
      // setCompanies(companiesRes || []);
      // setCategories(categoriesRes || []);
      // setLocations(locationsRes || []);

     setJobs(jobsRes.data || []);
      setCompanies(companiesRes.data || []);
      setCategories(categoriesRes.data || []);
      setLocations(locationsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data. Please refresh the page.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // const fetchFilteredJobs = async () => {
    const fetchFilteredJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...searchParams };
      
      // Add filter params
      if (filters.experience?.length > 0) {
        params.experience = filters.experience.join(',');
      }
      if (filters.jobType?.length > 0) {
        params.job_type = filters.jobType.join(',');
      }
      if (filters.industry?.length > 0) {
        params.industry = filters.industry.join(',');
      }
      if (filters.locationFilter) {
        params.location = filters.locationFilter;
      }
      if (filters.postedWithin) {
        params.posted_within = filters.postedWithin;
      }
      if (filters.salary?.length > 0) {
        // Parse salary ranges to get min/max
        const salaryMappings = {
          '0-3 LPA': { min: 0, max: 3 },
          '3-6 LPA': { min: 3, max: 6 },
          '6-10 LPA': { min: 6, max: 10 },
          '10-15 LPA': { min: 10, max: 15 },
          '15-25 LPA': { min: 15, max: 25 },
          '25-50 LPA': { min: 25, max: 50 },
          '50+ LPA': { min: 50, max: null },
        };
        const selectedSalaries = filters.salary.map(s => salaryMappings[s]).filter(Boolean);
        if (selectedSalaries.length > 0) {
          params.salary_min = Math.min(...selectedSalaries.map(s => s.min));
          const maxValues = selectedSalaries.map(s => s.max).filter(v => v !== null);
          if (maxValues.length > 0) {
            params.salary_max = Math.max(...maxValues);
          }
        }
      }
     

      const response = await jobsAPI.getAll(params);
       console.log("Jobs:", response);
      // setJobs(response || []);

      setJobs(response.data || []);
    } catch (error) {
      console.error('Error fetching filtered jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, searchParams]);

  useEffect(() => {
    // Fetch jobs whenever filters change
    fetchFilteredJobs();
    }, [fetchFilteredJobs]);
  // }, [filters]);

  const handleSearch = async ({ keyword, location, experience }) => {
    try {
      setLoading(true);
      const params = {};
      if (keyword) params.keyword = keyword;
      if (location) params.location = location;
      if (experience) params.experience = experience;

      setSearchParams(params);
      
      // Also apply existing filters
      if (filters.experience?.length > 0) {
        params.experience = filters.experience.join(',');
      }
      if (filters.jobType?.length > 0) {
        params.job_type = filters.jobType.join(',');
      }
      
      const response = await jobsAPI.getAll(params);
     // setJobs(response || []);
      setJobs(response.data || []);

    } catch (error) {
      console.error('Error searching jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to search jobs. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      experience: [],
      salary: [],
      jobType: [],
      industry: [],
      locationFilter: '',
      postedWithin: null,
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.experience?.length) count += filters.experience.length;
    if (filters.salary?.length) count += filters.salary.length;
    if (filters.jobType?.length) count += filters.jobType.length;
    if (filters.industry?.length) count += filters.industry.length;
    if (filters.postedWithin) count += 1;
    if (filters.locationFilter) count += 1;
    return count;
  };

  const getIconComponent = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : <Briefcase className="w-6 h-6" />;
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#4a90e2] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading jobs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Find your dream job now
            </h1>
            <p className="text-lg text-gray-600">
              100 + jobs for you to explore
            </p>
          </div>
          <SearchBar onSearch={handleSearch} />
        </div>
      </section>

      {/* Main Content with Filters */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {getActiveFilterCount() > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                  {getActiveFilterCount()}
                </span>
              )}
            </Button>
          </div>

          {/* Filters Sidebar */}
          <div className={`lg:w-72 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <JobFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              totalJobs={jobs.length}
            />
          </div>

          {/* Jobs List */}
          <div className="flex-1">
            {/* Active Filters Display */}
            {getActiveFilterCount() > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">Active filters:</span>
                {filters.experience?.map(exp => (
                  <span key={exp} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                    {exp}
                    <button onClick={() => handleFilterChange({
                      ...filters,
                      experience: filters.experience.filter(e => e !== exp)
                    })}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {filters.jobType?.map(type => (
                  <span key={type} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                    {type}
                    <button onClick={() => handleFilterChange({
                      ...filters,
                      jobType: filters.jobType.filter(t => t !== type)
                    })}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {filters.salary?.map(sal => (
                  <span key={sal} className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-sm rounded-full">
                    {sal}
                    <button onClick={() => handleFilterChange({
                      ...filters,
                      salary: filters.salary.filter(s => s !== sal)
                    })}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {filters.locationFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                    📍 {filters.locationFilter}
                    <button onClick={() => handleFilterChange({
                      ...filters,
                      locationFilter: ''
                    })}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-red-500 hover:text-red-600 h-7"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Jobs Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <TrendingUp className="w-6 h-6 mr-2 text-[#4a90e2]" />
                {getActiveFilterCount() > 0 ? 'Filtered Jobs' : 'Featured Jobs'}
                <span className="ml-2 text-lg font-normal text-gray-500">
                  ({jobs.length} {jobs.length === 1 ? 'job' : 'jobs'})
                </span>
              </h2>
            </div>

            {/* Jobs Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : jobs.length === 0 ? (
              <Card className="p-12 text-center">
                <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No jobs found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or search criteria</p>
                <Button onClick={handleClearFilters} variant="outline">
                  Clear all filters
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Companies */}
        {/*<section className="mb-16">
           <div className="flex items-center justify-between mb-6">
             <h2 className="text-2xl font-bold text-gray-800">Top Companies Hiring</h2>
            <Button variant="ghost" className="text-[#4a90e2] hover:text-[#3a7bc8]">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Button> 
          </div> 
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {companies.slice(0, 10).map((company) => (
              <Card
                key={company.id}
                className="p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group border border-gray-200 hover:border-[#4a90e2]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                    <span className="text-3xl font-bold text-[#4a90e2]">{company.name.charAt(0)}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-[#4a90e2] transition-colors">
                    {company.name}
                  </h3>
                  <p className="text-sm text-gray-600">{company.jobCount} Jobs</p>
                </div>
              </Card>
            ))}
          </div>
        </section> */}

        {/* Jobs by Category */}
        {/* <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Jobs by Category</h2>
            <Button variant="ghost" className="text-[#4a90e2] hover:text-[#3a7bc8]">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <Card
                key={category.id}
                className="p-5 hover:shadow-lg transition-all duration-300 cursor-pointer group border border-gray-200 hover:border-[#4a90e2]"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#4a90e2] group-hover:bg-[#4a90e2] group-hover:text-white transition-colors">
                    {getIconComponent(category.icon)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-[#4a90e2] transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">{category.jobCount.toLocaleString()} Jobs</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section> */}

        {/* Jobs by Location */}
        {/* <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <MapPin className="w-6 h-6 mr-2 text-[#4a90e2]" />
              Jobs by Location
            </h2>
            <Button variant="ghost" className="text-[#4a90e2] hover:text-[#3a7bc8]">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {locations.map((location) => (
              <Card
                key={location.id}
                className="p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group border border-gray-200 hover:border-[#4a90e2]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800 group-hover:text-[#4a90e2] transition-colors">
                      {location.name}
                    </h3>
                    <p className="text-sm text-gray-600">{location.jobCount.toLocaleString()} Jobs</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#4a90e2] transition-colors" />
                </div>
              </Card>
            ))}
          </div>
        </section> */}

        {/* Call to Action */}
        <section className="bg-gradient-to-br from-[#4a90e2] to-[#357abd] rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to take the next step?</h2>
          <p className="text-lg mb-6 opacity-90">
            Create your profile and get discovered by top companies
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" className="bg-white text-[#4a90e2] hover:bg-gray-100 font-semibold">
              Upload Resume
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4a90e2] font-semibold">
              Register Now
            </Button>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Home;